import { FileText, Paperclip, Trash2, Upload } from "lucide-react";
import { useRef, useState } from "react";
import { useDelete, useGetList, useNotify, useRefresh } from "ra-core";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import {
  ATTACHMENTS_BUCKET,
  attachmentPath,
} from "../../atomic-crm/providers/commons/attachments";
import { useSignedAttachment } from "../../atomic-crm/providers/commons/useSignedAttachment";

/** Exactly one subject, mirroring the DB check on `record_files`. */
export type FileSubject =
  | { contact_id: number }
  | { company_id: number }
  | { deal_id: number }
  | { job_id: string }
  | { task_id: number }
  | { estimate_id: string }
  | { invoice_id: string }
  | { expense_id: string };

interface RecordFile {
  id: string;
  storage_path: string;
  file_name: string;
  mime_type: string | null;
  size_bytes: number | null;
  created_at: string;
}

/** 25 MB, large enough for job photos and scanned PDFs, small enough that a
 *  mistaken video upload fails fast rather than after a long wait on 4G. */
const MAX_BYTES = 25 * 1024 * 1024;

const isImage = (mime: string | null) => Boolean(mime?.startsWith("image/"));

const prettySize = (bytes: number | null) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Files and photos on a record.
 *
 * Bytes go to the private `attachments` bucket under the active workspace's
 * folder; the row is only metadata. Both layers check the tenant
 * independently, so neither a forged row nor a forged path reaches another
 * workspace's files.
 *
 * If the metadata insert fails after the upload succeeds, the uploaded object
 * is removed, otherwise a failed attach would leave an orphan occupying
 * storage that no screen can ever show or delete.
 */
export const RecordFiles = ({
  subject,
  className,
  hideHeader,
}: {
  subject: FileSubject;
  className?: string;
  /** The parent card already names this section, do not repeat the title. */
  hideHeader?: boolean;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [deleteOne] = useDelete();
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: files, isPending } = useGetList<RecordFile>("record_files", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "created_at", order: "DESC" },
    filter: subject as Record<string, unknown>,
  });

  const upload = async (chosen: FileList | null) => {
    if (!chosen?.length) return;
    setBusy(true);
    const client = getSupabaseClient();

    for (const file of Array.from(chosen)) {
      if (file.size > MAX_BYTES) {
        notify(`${file.name} is larger than 25 MB`, { type: "warning" });
        continue;
      }

      let path: string;
      try {
        path = attachmentPath(file.name);
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : "Could not prepare the upload",
          { type: "error" },
        );
        break;
      }

      const stored = await client.storage
        .from(ATTACHMENTS_BUCKET)
        .upload(path, file, { contentType: file.type || undefined });
      if (stored.error) {
        notify(`${file.name}: ${stored.error.message}`, { type: "error" });
        continue;
      }

      const row = await client.from("record_files").insert({
        ...subject,
        storage_path: path,
        file_name: file.name,
        mime_type: file.type || null,
        size_bytes: file.size,
      });

      if (row.error) {
        // Roll the object back so a rejected attach cannot leave bytes behind
        // that nothing references.
        await client.storage.from(ATTACHMENTS_BUCKET).remove([path]);
        notify(`${file.name}: ${row.error.message}`, { type: "error" });
      }
    }

    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
    refresh();
  };

  const remove = (file: RecordFile) => {
    deleteOne(
      "record_files",
      { id: file.id, previousData: file },
      {
        mutationMode: "pessimistic",
        onSuccess: async () => {
          // Delete the row first: if RLS refuses it, the bytes must stay.
          await getSupabaseClient()
            .storage.from(ATTACHMENTS_BUCKET)
            .remove([file.storage_path]);
          refresh();
        },
        onError: (error: unknown) =>
          notify(
            error instanceof Error
              ? error.message
              : "Could not remove the file",
            { type: "error" },
          ),
      },
    );
  };

  const items = files ?? [];

  return (
    <div className={cn("flex min-w-0 flex-col gap-3", className)}>
      {hideHeader ? null : (
        <div className="flex items-center gap-2">
          <Paperclip
            className="size-4 text-muted-foreground"
            strokeWidth={1.75}
          />
          <h3 className="text-[14px] font-semibold tracking-tight text-foreground">
            Files &amp; photos
          </h3>
          {items.length ? (
            <span className="text-[12px] text-muted-foreground">
              {items.length}
            </span>
          ) : null}
        </div>
      )}

      <div
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          upload(event.dataTransfer.files);
        }}
        className={cn(
          "rounded-md border border-dashed px-3 py-4 text-center transition-colors",
          dragging ? "border-primary bg-primary/5" : "border-input",
        )}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(event) => upload(event.target.files)}
        />
        <Button
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => inputRef.current?.click()}
          className="gap-1.5"
        >
          <Upload className="size-3.5" />
          {busy ? "Uploading…" : "Add files"}
        </Button>
        <p className="mt-1.5 text-[12px] text-muted-foreground">
          Drag and drop, or choose files. Up to 25&nbsp;MB each.
        </p>
      </div>

      {isPending ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? null : (
        <ul className="flex flex-col gap-2">
          {items.map((file) => (
            <FileRow key={file.id} file={file} onRemove={() => remove(file)} />
          ))}
        </ul>
      )}
    </div>
  );
};

/** One file. The signed URL is resolved per row, so the hook lives here. */
const FileRow = ({
  file,
  onRemove,
}: {
  file: RecordFile;
  onRemove: () => void;
}) => {
  const href = useSignedAttachment(file.storage_path);

  return (
    <li className="flex min-w-0 items-center gap-2.5">
      {isImage(file.mime_type) && href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0"
        >
          <img
            src={href}
            alt={file.file_name}
            className="size-10 rounded border border-border object-cover"
          />
        </a>
      ) : (
        <FileText
          className="size-5 shrink-0 text-muted-foreground"
          strokeWidth={1.75}
        />
      )}

      <div className="min-w-0 flex-1">
        <a
          href={href || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-[13px] font-medium text-foreground no-underline hover:underline"
        >
          {file.file_name}
        </a>
        <span className="text-[11.5px] text-muted-foreground">
          {prettySize(file.size_bytes)}
        </span>
      </div>

      <button
        aria-label={`Remove ${file.file_name}`}
        className="shrink-0 text-muted-foreground transition-colors hover:text-destructive"
        onClick={onRemove}
      >
        <Trash2 className="size-3.5" />
      </button>
    </li>
  );
};
